import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { useTick } from "../hooks/useTick";

const LINES = ["Precision", "Medical", "Logistics."];
const ease = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const ref = useRef(null);
  const tick = useTick();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (id) => {
    tick(660);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden pt-24 pb-16">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1637154691927-5effcaf27e61"
          alt="Abstract medical DNA"
          className="w-full h-full object-cover grayscale opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/30" />
      </motion.div>

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-2 h-2 bg-accent animate-pulse" />
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">
              Chain-of-Custody Certified · GA
            </span>
          </motion.div>

          <h1 data-testid="hero-title" className="font-display font-black tracking-tighter uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease, delay: 0.3 + i * 0.12 }}
                >
                  {i === 2 ? <span className="text-primary">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="lg:col-span-4 space-y-8"
        >
          <p className="text-base md:text-lg font-mono text-muted-foreground leading-relaxed border-l border-border pl-5">
            Medical courier, DNA & drug testing, and mobile notary services — delivered with clinical precision and secured custody, every mile.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("booking")}
              className="bg-primary text-primary-foreground font-mono uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 border border-primary hover:bg-transparent hover:text-primary transition-colors duration-300"
            >
              Request Service
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              className="font-mono uppercase tracking-[0.15em] text-xs font-bold py-4 px-8 border border-border hover:border-primary hover:text-primary transition-colors duration-300"
            >
              Our Services
            </button>
          </div>
        </motion.div>
      </div>

      <motion.button
        style={{ opacity }}
        onClick={() => scrollTo("stats")}
        data-testid="scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
};
